import { useState } from "react";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/common/Breadcrumb";

const FORM_URL = "https://readdy.ai/api/form/da22andv7gurs51ef4b0";

export default function Contact() {
  useSeo({
    title: "联系我们 - 我爱漫画网 | 投稿推荐与内容合作",
    description:
      "联系我爱漫画网，投稿推荐你喜欢的高分漫画与冷门佳作，或洽谈内容合作。提交联系方式与留言，我们会尽快回复。",
    path: "/contact",
  });

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾：命中蜜罐字段则模拟正常成功反馈，不真正提交
    const honeypot = (data.get("website_alt") || "").toString().trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (key === "website_alt") continue;
      payload.append(key, value.toString());
    }

    setSubmitting(true);
    setStatus("idle");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        const errText = typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : "提交失败，请稍后再试。";
        setFormError(errText);
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后再试。");
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 - 我爱漫画网",
    description: "投稿推荐与内容合作的联系渠道",
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactSchema) }} />

      <section className="bg-background-100">
        <div className="container-page flex flex-col items-center py-12 text-center md:py-16">
          <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-sm font-medium text-accent-700">
            <i className="ri-mail-send-line" />
            联系我们
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">想投稿推荐或合作？</h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            如果你有想安利给全世界的漫画，或想与我爱漫画网洽谈内容合作，欢迎留下联系方式，我们会尽快回复。
          </p>
        </div>
      </section>

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <section className="container-page pb-12 md:pb-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px] md:gap-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">提交你的信息</h2>
            <p className="mt-1.5 text-sm text-foreground-500">带 * 的为必填项，我们会在收到后尽快回复。</p>

            {status === "success" && (
              <div className="mt-5 flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                <i className="ri-checkbox-circle-line mt-0.5 text-lg" />
                <div>
                  <p className="font-bold">提交成功</p>
                  <p className="mt-0.5">感谢你的来信，我们已收到信息，会尽快与你联系。</p>
                </div>
              </div>
            )}
            {status === "error" && (
              <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-line mt-0.5 text-lg" />
                <div>
                  <p className="font-bold">提交失败</p>
                  <p className="mt-0.5">{formError || "请检查填写内容后重试。"}</p>
                </div>
              </div>
            )}

            <form
              className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2"
              onSubmit={handleSubmit}
              data-readdy-form="我爱漫画网联系表单"
            >
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground-800">
                  称呼 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  maxLength={30}
                  placeholder="怎么称呼你"
                  className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                  邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  maxLength={80}
                  placeholder="name@example.com"
                  className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <label htmlFor="subject" className="text-sm font-medium text-foreground-800">
                  主题
                </label>
                <select
                  id="subject"
                  name="subject"
                  className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  defaultValue="投稿推荐漫画"
                >
                  <option>投稿推荐漫画</option>
                  <option>内容合作</option>
                  <option>意见反馈</option>
                  <option>其他</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <label htmlFor="message" className="flex items-center justify-between text-sm font-medium text-foreground-800">
                  <span>留言 <span className="text-primary-600">*</span></span>
                  <span className="text-xs font-normal text-foreground-400">最多 500 字</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="想推荐哪部漫画？或者想聊点什么？"
                  className="resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              {/* 反垃圾蜜罐字段 */}
              <div className="alt-honeypot-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      提交留言
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg bg-foreground-950 p-6 text-background-50">
              <h2 className="font-heading text-lg font-bold">联系方式</h2>
              <ul className="mt-4 space-y-4 text-sm text-background-100">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-lg text-background-50">
                    <i className="ri-mail-line" />
                  </span>
                  <div>
                    <p className="text-xs text-background-300">电子邮箱</p>
                    <p className="font-medium">hello@woaimanhua.example</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-lg text-foreground-950">
                    <i className="ri-time-line" />
                  </span>
                  <div>
                    <p className="text-xs text-background-300">回复时间</p>
                    <p className="font-medium">工作日 48 小时内</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-500 text-lg text-background-50">
                    <i className="ri-question-line" />
                  </span>
                  <div>
                    <p className="text-xs text-background-300">常见问题</p>
                    <a href="/faq" className="font-medium underline-offset-2 hover:underline">先去 FAQ 看看</a>
                  </div>
                </li>
              </ul>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">投稿小提示</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                投稿推荐时，尽量写明作品名称、作者以及你的推荐理由，我们会试读审核后补充进对应栏目与榜单。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}